import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { User, Organization } from './types';
import api from './services/api';

// Components
import Login from './components/Login';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import ModularDashboard from './components/ModularDashboard';
import UserManagement from './components/UserManagement';
import PersonalScorecard from './components/PersonalScorecard';
import TeamScorecard from './components/TeamScorecard';
import CommissionCalculator from './components/CommissionCalculator';
import OrganizationSelector from './components/OrganizationSelector';
import Analytics from './components/Analytics';
import Timeclock from './components/Timeclock';
import TimeclockAdmin from './components/TimeclockAdmin';
import WeeklyPlanning from './components/WeeklyPlanning';
import DynamicTrends from './components/DynamicTrends';
import MySalesTracker from './components/MySalesTracker';
import EmbedSales from './components/EmbedSales';
import SalesAuditTool from './components/SalesAuditTool';
import CallQualityAnalyzer from './components/CallQualityAnalyzer';
import KnowledgePortal from './components/KnowledgePortal';
import TeamComparison from './components/TeamComparison';
import CRM from './components/CRM';
import AgentEfficiency from './components/AgentEfficiency';
import EnhancedAgentEfficiency from './components/EnhancedAgentEfficiency';
import PartnerEfficiency from './components/PartnerEfficiency';
import PartnerAnalytics from './components/PartnerAnalytics';
import Five9Teams from './components/Five9Teams';
import Escalations from './components/Escalations';
import PreviousWeekCoaching from './components/PreviousWeekCoaching';
import ModulePermissionsAdmin from './components/ModulePermissionsAdmin';
import EmbedPhone from './components/EmbedPhone';
import SpiffManager from './components/SpiffManager';

import './App.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4B88A2',
      dark: '#0C0C0C',
      light: '#6BA5BD',
    },
    secondary: {
      main: '#4B88A2',
    },
    background: {
      default: '#F8F9FA',
      paper: '#ffffff',
    },
    text: {
      primary: '#0C0C0C',
      secondary: '#4B88A2',
    },
  },
  typography: {
    fontFamily: '"Inter", "Archivo", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.5px',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.3px',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.3px',
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const handleLogin = (role: string, userData: User) => {
    setUserRole(role);
    setUser(userData);
    localStorage.setItem('userRole', role);
    localStorage.setItem('user', JSON.stringify(userData));
    // Always land on the modular dashboard after a fresh login, regardless of
    // which URL the browser was on (e.g. /embed-phone from a previous session).
    // The Router mounts right after this, so it picks up '/' as the location.
    window.history.replaceState(null, '', '/');
  };

  const handleLogout = () => {
    setUserRole(null);
    setUser(null);
    setSelectedOrg(null);
    localStorage.removeItem('userRole');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrg');
    localStorage.removeItem('token');
  };

  const handleSelectOrg = (org: Organization | { id: string; name: string }) => {
    setSelectedOrg(org as Organization);
    localStorage.setItem('selectedOrg', JSON.stringify(org));
    
    // Navigate to the appropriate route
    if (org.id === 'user-management') {
      window.location.href = '/user-management';
    } else if (org.id === 'module-permissions') {
      window.location.href = '/module-permissions';
    } else if (org.id === 'crm') {
      window.location.href = '/crm';
    } else {
      // Regular organization - go to home/dashboard
      window.location.href = '/';
    }
  };

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    const storedUser = localStorage.getItem('user');
    const storedOrg = localStorage.getItem('selectedOrg');
    
    if (storedRole && storedUser) {
      setUserRole(storedRole);
      setUser(JSON.parse(storedUser));
      if (storedOrg) {
        setSelectedOrg(JSON.parse(storedOrg));
      }
    }
  }, []);

  // Fetch and auto-select organization when user logs in
  useEffect(() => {
    const fetchAndSetOrganization = async () => {
      if (user && !selectedOrg) {
        try {
          const response = await api.getOrganizations();
          
          // Handle wrapped response {success: true, organizations: [...]}
          const orgs = Array.isArray(response) ? response : (response.organizations || []);
          
          // For master users and superusers, store all orgs for selection
          if (user.is_master || userRole === 'superuser') {
            setOrganizations(orgs);
          } else {
            // For regular users, auto-select their organization
            const orgId = user.organizationId || (user as any).organization_id;
            
            if (orgId) {
              const userOrg = orgs.find((org: Organization) => org.id === orgId);
              if (userOrg) {
                setSelectedOrg(userOrg);
                localStorage.setItem('selectedOrg', JSON.stringify(userOrg));
              }
            } else {
              // Fallback: Check if user has Direct Meds in their organizations
              const directMedsOrg = orgs.find((org: Organization) => org.name === 'Direct Meds');
              if (directMedsOrg) {
                setSelectedOrg(directMedsOrg);
                localStorage.setItem('selectedOrg', JSON.stringify(directMedsOrg));
              } else if (orgs.length === 1) {
                // If only one org exists, select it
                setSelectedOrg(orgs[0]);
                localStorage.setItem('selectedOrg', JSON.stringify(orgs[0]));
              }
            }
          }
        } catch (error) {
          console.error('Error fetching organizations:', error);
        }
      }
    };
    fetchAndSetOrganization();
  }, [user, selectedOrg]);

  if (!userRole || !user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  // If user is superuser/master and no org selected, show org selector
  if ((user.is_master || userRole === 'superuser') && !selectedOrg) {
    // Show loading if organizations haven't been fetched yet
    if (organizations.length === 0) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div>Loading organizations...</div>
          </div>
        </ThemeProvider>
      );
    }
    
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <OrganizationSelector 
            user={user} 
            userRole={userRole || ''} 
            onSelectOrganization={handleSelectOrg} 
          />
        </ThemeProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <div className="App">
            <Navbar 
              userRole={userRole} 
              user={user}
              selectedOrg={selectedOrg}
              onLogout={handleLogout}
              onBackToOrgSelector={(user.is_master || userRole === 'superuser') ? () => {
                setSelectedOrg(null);
                localStorage.removeItem('selectedOrg');
              } : null}
            />
            <main className="main-content">
              <Routes>
                <Route 
                  path="/" 
                  element={
                    // Check if selectedOrg is a special admin page (user-management, module-permissions)
                    // If so, redirect back to org selector
                    selectedOrg && (String(selectedOrg.id) === 'user-management' || String(selectedOrg.id) === 'module-permissions' || String(selectedOrg.id) === 'crm') 
                      ? <Navigate to={`/${selectedOrg.id}`} replace />
                      : <ModularDashboard 
                          user={user} 
                          userRole={userRole} 
                          selectedOrg={selectedOrg}
                        />
                  } 
                />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/personal-scorecard" element={<PersonalScorecard />} />
                <Route path="/team-scorecard" element={<TeamScorecard />} />
                <Route path="/commission" element={<CommissionCalculator />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/timeclock" element={<Timeclock />} />
                <Route path="/timeclock-admin" element={<TimeclockAdmin />} />
                <Route path="/weekly-planning" element={<WeeklyPlanning />} />
                <Route path="/dynamic-trends" element={<DynamicTrends />} />
                <Route path="/my-sales" element={<MySalesTracker />} />
                <Route path="/embed-sales" element={<EmbedSales />} />
                <Route path="/sales-audit" element={<SalesAuditTool />} />
                <Route path="/call-quality" element={<CallQualityAnalyzer />} />
                <Route path="/knowledge-portal" element={<KnowledgePortal />} />
                <Route path="/team-comparison" element={<TeamComparison />} />
                <Route path="/crm/*" element={<CRM />} />
                <Route path="/agent-efficiency" element={<AgentEfficiency />} />
                <Route path="/enhanced-efficiency" element={<EnhancedAgentEfficiency />} />
                <Route path="/partner-efficiency" element={<PartnerEfficiency />} />
                <Route path="/partner-analytics" element={<PartnerAnalytics />} />
                <Route path="/five9-teams" element={<Five9Teams />} />
                <Route path="/escalations" element={<Escalations />} />
                <Route path="/coaching" element={<PreviousWeekCoaching />} />
                <Route path="/module-permissions" element={<ModulePermissionsAdmin />} />
                <Route path="/embed-phone" element={<EmbedPhone />} />
                <Route path="/spiff" element={<SpiffManager />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
